/**
 * R6.3 PHASE 2.1 — dashboard operational-context isolation.
 *
 * The selected SITE is the single context authority for every site/sector-scoped
 * Dashboard block. These pure helpers encode that rule so the clone can never
 * mix two sites in one selected-site operational context:
 *
 *   - a site's sector grid contains only that site's sectors;
 *   - changing site never retains a sector that belongs to another site;
 *   - an alert is attributed to a sector only through the canonical
 *     node → sector_id → placement join — never by name guesswork, and never
 *     fanned out farm-wide onto every sector card.
 *
 * Pure functions only — no React, no network, no side effects.
 */

import type { IoTNode } from '../../lib/iotApi';
import type { SectorPlacement } from '../../lib/operationalOverview';
import type { SoverdiAlert, SoverdiSectorView } from './agriSafeDashboardAdapter';

/** Sectors of one site, in canonical order. No cross-site fallback. */
export function sectorsForSite(
  sectors: SoverdiSectorView[],
  siteName: string,
): SoverdiSectorView[] {
  return sectors.filter((sector) => sector.siteName === siteName);
}

/**
 * Resolve the selected sector for the CURRENT site.
 *
 * The previously selected sector is kept only when it still belongs to the new
 * site; otherwise selection falls deterministically to the site's first sector,
 * or to an empty string (truthful no-sector state).
 */
export function resolveSelectedSectorId(
  siteSectors: SoverdiSectorView[],
  previousId: string | null,
): string {
  if (previousId && siteSectors.some((sector) => sector.id === previousId)) {
    return previousId;
  }
  return siteSectors[0]?.id ?? '';
}

/** Ordered, de-duplicated site names from the site models (topology order). */
export function orderedSiteNames(models: ReadonlyArray<{ siteName: string }>): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const model of models) {
    if (!seen.has(model.siteName)) {
      seen.add(model.siteName);
      names.push(model.siteName);
    }
  }
  return names;
}

/**
 * Where an alert belongs, resolved through the canonical join.
 *
 * `site`      — the alert's node resolves to a sector of the selected site.
 * `otherSite` — the alert's node resolves to a sector of a DIFFERENT site.
 * `farmWide`  — no node match, or a node without a canonical sector binding
 *               (relay/pump equipment, or an unmapped sector id). These must
 *               surface only in the global alerts block, never on a sector card.
 */
export type AlertScope = 'site' | 'otherSite' | 'farmWide';

export interface ScopedAlert {
  alert: SoverdiAlert;
  scope: AlertScope;
  /** Canonical site the alert's node resolves to, or null when unscoped. */
  siteName: string | null;
  /** Canonical sector view id (`site:code`), or null when unscoped. */
  sectorId: string | null;
}

function resolveNodeByName(nodes: IoTNode[], source: string): IoTNode | undefined {
  return nodes.find((node) => node.name === source || node.id === source);
}

/**
 * Attribute every alert to a site/sector using only the canonical join.
 *
 * `alert.source` is the backend `node_name`. It is matched to a node, then the
 * node's `sector_id` is resolved through the shared placement map. Anything
 * that cannot be resolved this way is `farmWide` — never guessed onto a sector.
 */
export function scopeAlerts(input: {
  alerts: SoverdiAlert[];
  nodes: IoTNode[];
  placement: Map<string, SectorPlacement>;
  siteName: string;
}): ScopedAlert[] {
  const { alerts, nodes, placement, siteName } = input;
  return alerts.map((alert) => {
    const source = alert.source;
    if (!source) {
      return { alert, scope: 'farmWide' as const, siteName: null, sectorId: null };
    }
    const node = resolveNodeByName(nodes, source);
    if (!node) {
      return { alert, scope: 'farmWide' as const, siteName: null, sectorId: null };
    }
    const resolved = node.sector_id ? placement.get(node.sector_id) : undefined;
    if (!resolved) {
      return { alert, scope: 'farmWide' as const, siteName: null, sectorId: null };
    }
    const sectorId = `${resolved.siteName}:${resolved.sectorCode}`;
    if (resolved.siteName === siteName) {
      return { alert, scope: 'site' as const, siteName: resolved.siteName, sectorId };
    }
    return { alert, scope: 'otherSite' as const, siteName: resolved.siteName, sectorId };
  });
}

/**
 * Alert count display value. An unavailable alerts read renders an em dash and
 * is NEVER collapsed into a "0 alerts" state.
 */
export function alertKpiValue(unacknowledgedCount: number, unavailable: boolean): string {
  return unavailable ? '—' : String(unacknowledgedCount);
}
